import { ContentfulLocale, MainNavigationSection } from '../../types'
import {
  isTypeNews12NewYork,
  TypeCollectionSkeleton,
  TypeHomepageSkeleton,
  TypeListSkeleton,
  TypeLivePageSkeleton,
  TypeMenu,
  TypeNavigation,
  TypeNews12NewYorkSkeleton,
  TypePageSkeleton,
  TypeStorySkeleton,
  TypeWeatherConditionsSkeleton
} from '../../types/contentful'
import { NavigationLinksObjectSection } from '../../types/contentful/TypeNavigation'
import { isTypePageV2, TypePageV2Skeleton } from '../../types/contentful/TypePage'
import { isValidLinksObjectItem } from '../../types/linksObject'

import { Entry } from 'contentful'

const getPath = entry => {
  switch (entry.sys.contentType.sys.id) {
    case 'collection':
      return `/category/${entry.fields?.slug}`

    case 'page':
      return `/pages/${entry.fields?.slug}`

    case 'page-v2':
      return `/shows/${entry.fields?.slug}`

    case 'news12NewYork':
      return `https://newyork.news12.com`

    default:
      return `/${entry.fields?.slug || ''}`
  }
}

export type FooterField = {
  path: string
  name: string
  isRoot?: boolean
}

export type NavigationFooter = {
  [key: string]: FooterField[]
}

export type NavigationData = {
  header: MainNavigationSection[]
  footer: NavigationFooter
  entryIDs?: (string | undefined)[]
}

type SkeletonsWithTitle =
  | TypeCollectionSkeleton
  | TypeHomepageSkeleton
  | TypeListSkeleton
  | TypeLivePageSkeleton
  | TypePageSkeleton
  | TypeStorySkeleton
  | TypeWeatherConditionsSkeleton
  | TypePageV2Skeleton
type EntriesWithTitle = Entry<SkeletonsWithTitle, 'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>
type SkeletonsWithPageTitle = TypeNews12NewYorkSkeleton
type Link = Entry<SkeletonsWithTitle | SkeletonsWithPageTitle, 'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>

const getTitle = (link: Link): string => {
  if (isTypeNews12NewYork(link)) {
    return (link.fields?.pageTitle || '') as string
  } else if (isTypePageV2(link)) {
    return (link.fields?.name ?? '') as string
  } else {
    return (link as EntriesWithTitle).fields?.title ?? ''
  }
}

const DEFAULT_NAVIGATION_FOOTER: NavigationData['footer'] = {
  bottom: [] as FooterField[]
}

export const extractFooter = (objectLink: Record<string, NavigationLinksObjectSection>) => {
  const result: NavigationFooter = {}
  for (const [section, items] of Object.entries(objectLink)) {
    result[section] = Object.entries(items || {}).map(([key, value]) => ({
      name: key,
      path: value.path || '',
      isRoot: value.isRoot || false
    }))
  }
  return result
}

const extractNavigation = (data: TypeMenu<'WITHOUT_UNRESOLVABLE_LINKS', ContentfulLocale>[]): NavigationData => {
  const entryIDs: (string | undefined)[] = []

  const header =
    data?.[0]?.fields.links
      ?.filter(tabItem => {
        entryIDs.push(tabItem?.sys?.id)

        return tabItem?.fields?.links
      })
      .map(tabItem => {
        return {
          title: tabItem?.fields?.title ?? '',
          items:
            tabItem?.fields?.links?.map(menuItem => ({
              title: getTitle(menuItem!),
              path: getPath(menuItem)
            })) ?? []
        }
      }) ?? []

  const footer =
    (data[0]?.fields?.links
      ?.map(tabItem => tabItem?.fields?.linksObject)
      ?.filter((footer): footer is Record<string, NavigationLinksObjectSection> => Boolean(footer))
      ?.map(footer => extractFooter(footer))
      ?.find(footer => footer) as NavigationFooter) || DEFAULT_NAVIGATION_FOOTER

  return { header, footer, entryIDs }
}

export type MarketingNavigationItem = {
  name: string
  href: string
}

export const extractMarketingNavigation = (
  data: TypeNavigation<'WITHOUT_UNRESOLVABLE_LINKS', ContentfulLocale>[]
): MarketingNavigationItem[] => {
  const result: MarketingNavigationItem[] = []

  const links = data[0]?.fields.links ?? []

  for (const item of links) {
    if (item) {
      result.push({
        name: getTitle(item),
        href: getPath(item)
      })
    }
  }

  const linksObject = data[0]?.fields.linksObject ?? {}

  for (const [name, item] of Object.entries(linksObject)) {
    if (isValidLinksObjectItem(item)) {
      result.push({
        name,
        href: item.path
      })
    }
  }

  return result
}

export default extractNavigation
